import {gql} from '@apollo/client';
import {pick} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions, convertCollaborationsToCollaborators} from 'auth/PermissionConstants';
import {WORKLISTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OwnableInfo, UpdateOwnableParams} from 'create-components/OwnableConstants';
import {
  OwnableSettingsModalProps,
  OwnableSettingsWithCollaborationsModal,
} from 'create-components/OwnableSettingsModal';
import {toServerCollaborators} from 'create-components/OwnableUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getStore} from 'redux-stores/Store';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {asJQueryPromise} from 'util/Promises';
import {
  WorklistSettingsOwnerAndCollaboratorsQuery,
  WorklistSettingsOwnerAndCollaboratorsQueryVariables,
} from 'worklists/__generated/WorklistSettingsOwnerAndCollaboratorsQuery';
import {RefreshOptions, UpdateWorklistParams, updateWorklistAsync} from 'worklists/WorklistActions';
import {WORKLIST_DISABLED_OWNER_REASON} from 'worklists/WorklistConstants';

export interface WorklistSettingsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  refreshOptions?: RefreshOptions;
  worklistId: string;
  worklistOwnerAndCollaboratorInfo: OwnableInfo;
  permissions: Permissions;
}

export const WORKLIST_SETTINGS_OWNER_AND_COLLABORATORS_QUERY: DocumentNode = gql`
  query WorklistSettingsOwnerAndCollaboratorsQuery($worklistId: StrictString!)
  @skipAudit
  @engComponent(name: WORKLISTS) {
    worklist(id: $worklistId) {
      ... on OwnableInterface {
        ...OwnerAndCollaboratorInfoGraphQL
        ...HasOwnablePermissionsGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('OwnerAndCollaboratorInfo').fragment}
  ${getFragmentWrapper('HasOwnablePermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function QueryingWorklistSettingsModal(
  props: Omit<WorklistSettingsModalProps, 'worklistOwnerAndCollaboratorInfo' | 'permissions'>
): JSX.Element {
  return (
    <BenchlingQuery<
      WorklistSettingsOwnerAndCollaboratorsQuery,
      WorklistSettingsOwnerAndCollaboratorsQueryVariables
    >
      query={WORKLIST_SETTINGS_OWNER_AND_COLLABORATORS_QUERY}
      // In practice, user should not need to wait on this data due to pre-fetching in WorklistSettingsIcon
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{worklistId: props.worklistId}}
    >
      {(data) => {
        assert(
          !isInaccessible(data.worklist),
          'Expected worklist to be accessible in WorklistSettingsModal because we were already able to load it in the sidepanel'
        );

        const {worklist} = data;
        const worklistOwnerAndCollaboratorInfo: OwnableInfo = {
          id: worklist.id,
          name: worklist.name,
          owner: worklist.ownerJson,
          ownerInfo: worklist.ownerInfoJson,
          collaborators: worklist.collaboratorsJson,
        };

        const worklistPermissions: Permissions = {
          ...pick(worklist.permissions, 'admin', 'appendable', 'readable', 'writable'),
          owner: worklist.permissions.owner ?? undefined,
        };
        return (
          <WorklistSettingsModal
            {...props}
            worklistOwnerAndCollaboratorInfo={worklistOwnerAndCollaboratorInfo}
            permissions={worklistPermissions}
          />
        );
      }}
    </BenchlingQuery>
  );
}
/**
 * Modal component to update a worklist. Allows user to change the worklist name and collaborators.
 */
function WorklistSettingsModal(props: WorklistSettingsModalProps): JSX.Element {
  useOnMount(() => {
    analytics.deprecatedTrackEvent(WORKLISTS_ANALYTICS_ACTIONS.OPENED_SETTINGS_MODAL);
  });

  function updateWorklistWithCollaborators(data: UpdateOwnableParams): JQueryPromise<void> {
    const clientCollaborators =
      data.collaborators ??
      convertCollaborationsToCollaborators((data.collaborations ?? []).filter((c) => !c.fromOwnership));
    const serverCollaborators = toServerCollaborators(clientCollaborators);
    const worklistNameChanged = props.worklistOwnerAndCollaboratorInfo.name !== data.name;
    const newData: UpdateWorklistParams = {};
    if (worklistNameChanged) {
      newData.worklistName = data.name;
    }
    if (data.collaborators !== undefined || data.collaborations !== undefined) {
      newData.collaborators = serverCollaborators;
    }

    return asJQueryPromise(
      updateWorklistAsync(
        props.worklistId,
        newData,
        props.refreshOptions,
        getStore().dispatch.bind(getStore())
      )
    );
  }

  const ownableSettingsProps: OwnableSettingsModalProps = {
    ownableType: ModelTypeConstants.WORKLIST,
    existingOwnable: props.worklistOwnerAndCollaboratorInfo,
    onRequestClose: props.onRequestClose,
    navigateOnSuccess: false,
    doSubmit: updateWorklistWithCollaborators,
    enableDescription: false,
    isReadOnly: !props.permissions.admin,
    requiresCreatorOwnership: true,
    disableChangeOwner: true,
    disabledOwnerTooltipText: WORKLIST_DISABLED_OWNER_REASON,
    ownableId: props.worklistId,
  };

  return <OwnableSettingsWithCollaborationsModal {...ownableSettingsProps} />;
}

export default QueryingWorklistSettingsModal;
